<template>
  <div class="visit-detail">
    <div class="visit-summary">
      <div><small>拜访编号</small><strong>{{ visit.visit_no || '—' }}</strong><span>本页为已提交的正式拜访记录</span></div>
      <span :class="statusClass(compliance.status)">{{ complianceStatusLabel(compliance.status) }}</span>
    </div>

    <h3 class="detail-section-title">拜访信息</h3>
    <dl class="visit-facts">
      <div class="visit-fact"><dt>拜访时间</dt><dd>{{ displayTime(visit.visit_time) }}</dd></div>
      <div class="visit-fact"><dt>拜访方式</dt><dd>{{ visit.visit_method || '—' }}</dd></div>
      <div class="visit-fact"><dt>记录状态</dt><dd>{{ visitStatusLabel(visit.status) }}</dd></div>
      <div class="visit-fact"><dt>录入来源</dt><dd>{{ entryModeLabel(visit.visit_entry_mode) }}</dd></div>
      <div class="visit-fact"><dt>省区</dt><dd>{{ visit.submitted_region_name || (visit.region && visit.region.entity_name) || '—' }}</dd></div>
      <div class="visit-fact"><dt>医院</dt><dd>{{ visit.hospital_name || (visit.hospital && visit.hospital.entity_name) || '—' }}</dd></div>
      <div class="visit-fact"><dt>科室</dt><dd>{{ visit.department_name || (visit.department && visit.department.entity_name) || '—' }}</dd></div>
      <div class="visit-fact"><dt>医生</dt><dd>{{ visit.doctor_name || (visit.doctor && visit.doctor.entity_name) || '—' }}</dd></div>
      <div class="visit-fact"><dt>产品</dt><dd>{{ visit.product_name || '—' }}</dd></div>
      <div class="visit-fact"><dt>参与代表</dt><dd>{{ visit.participants || '—' }}</dd></div>
      <div class="visit-fact"><dt>是否预约</dt><dd>{{ yesNo(visit.is_appointment) }}</dd></div>
    </dl>

    <h3 class="detail-section-title">拜访内容</h3>
    <div class="visit-text-card summary-card">
      <h4>内容摘要</h4>
      <div class="detail-text">{{ visit.content_summary || visit.visit_content || (visit.ai_display && visit.ai_display.content_summary) || '—' }}</div>
    </div>
    <div class="visit-text-card">
      <h4>识别原文</h4>
      <div class="detail-text">{{ (visit.ai_display && visit.ai_display.original_transcript) || visit.original_text || '—' }}</div>
    </div>

    <h3 class="detail-section-title">定位信息</h3>
    <dl class="visit-facts">
      <div class="visit-fact"><dt>定位坐标</dt><dd>{{ coordinates }}</dd></div>
      <div class="visit-fact"><dt>定位时间</dt><dd>{{ displayTime(visit.gps_time) }}</dd></div>
      <div class="visit-fact"><dt>定位精度</dt><dd>{{ visit.location_accuracy == null ? '—' : visit.location_accuracy + ' 米' }}</dd></div>
      <div class="visit-fact"><dt>AI 识别地点</dt><dd>{{ visit.ai_location || '—' }}</dd></div>
    </dl>

    <h3 class="detail-section-title">合规校验</h3>
    <p class="section-explain">拜访状态表示记录是否已提交；合规结论是检查留痕。已完成的记录仍可能显示“校验未通过”，两者含义不同。</p>
    <dl class="visit-facts">
      <div class="visit-fact"><dt>总体结论</dt><dd><span :class="statusClass(compliance.status)">{{ complianceStatusLabel(compliance.status) }}</span></dd></div>
      <div class="visit-fact"><dt>未通过项数</dt><dd>{{ compliance.failCount }} 项</dd></div>
    </dl>
    <div class="visit-text-card compliance-summary">
      <h4>未通过摘要</h4>
      <div class="detail-text">{{ compliance.failSummary || '无' }}</div>
    </div>
    <p class="section-explain">GPS 定位按本条记录已保存的经纬度判断，有有效经纬度即通过。其余校验项按各轮记录展示，空白表示该轮没有记录相应信息。</p>
    <div v-for="group in complianceGroups" :key="group.seq" class="round-card">
      <div class="round-title">第 {{ group.seq }} 轮校验 <small>历史结果仅展示，不在此处修改</small></div>
      <el-table :data="group.items" size="small" border>
        <el-table-column label="校验项" min-width="130"><template slot-scope="{row}">{{ row.check_name || checkName(row.check_code) }}</template></el-table-column>
        <el-table-column label="结果" width="115"><template slot-scope="{row}"><span :class="statusClass(row.check_status)">{{ checkStatusLabel(row.check_status) }}</span></template></el-table-column>
        <el-table-column label="校验显示值" min-width="150"><template slot-scope="{row}">{{ row.display_value || '—' }}</template></el-table-column>
        <el-table-column label="未通过原因" min-width="170"><template slot-scope="{row}">{{ row.fail_reason || '—' }}</template></el-table-column>
        <el-table-column label="判断依据" min-width="170"><template slot-scope="{row}">{{ row.rule_reference || row.evidence_summary || '—' }}</template></el-table-column>
      </el-table>
    </div>
    <el-empty v-if="!complianceGroups.length" description="暂无合规校验历史" />
  </div>
</template>

<script>
import { visitStatusLabel, complianceStatusLabel, checkStatusLabel, hasVisitLocation, visitComplianceDisplay } from '../utils/visit-display'

const checkInfo = {
  SENSITIVE_WORD: '敏感词',
  SEMANTIC_VIOLATION: '语义合规',
  FILING: '备案',
  PRODUCT_AUTH: '产品授权'
}

export default {
  name: 'VisitDetail',
  props: { visit: { type: Object, required: true } },
  computed: {
    compliance () { return visitComplianceDisplay(this.visit) },
    coordinates () {
      return hasVisitLocation(this.visit) ? `${this.visit.latitude}, ${this.visit.longitude}` : '未获取'
    },
    complianceGroups () {
      const map = {}
      this.compliance.checks.forEach(item => {
        if (!map[item.check_seq]) map[item.check_seq] = []
        map[item.check_seq].push(item)
      })
      return Object.keys(map).sort((a, b) => Number(b) - Number(a)).map(seq => ({ seq, items: map[seq] }))
    }
  },
  methods: {
    visitStatusLabel, complianceStatusLabel, checkStatusLabel,
    checkName (code) { return checkInfo[code] || '其他校验项' },
    entryModeLabel (value) { return { AI_VOICE: 'AI 语音生成', AI_VOICE_MANUAL: 'AI 语音＋人工补充', MANUAL: '手工填报' }[value] || '历史记录' },
    yesNo (value) { return value == null ? '未记录' : value === true || value === 1 || value === '1' ? '是' : '否' },
    statusClass (value) {
      const status = String(value || '').toUpperCase()
      if (['PASS', 'MATCHED', 'COMPLETED'].includes(status)) return 'tag-pass'
      if (['FAIL', 'CONFLICT'].includes(status)) return 'tag-fail'
      return 'tag-wait'
    },
    displayTime (value) {
      if (!value) return '—'
      const match = /\/Date\((-?\d+)(?:[+-]\d{4})?\)\//.exec(String(value))
      const date = match ? new Date(Number(match[1])) : new Date(String(value).replace(' ', 'T'))
      if (Number.isNaN(date.getTime())) return String(value)
      const two = number => String(number).padStart(2, '0')
      return `${date.getFullYear()}-${two(date.getMonth() + 1)}-${two(date.getDate())} ${two(date.getHours())}:${two(date.getMinutes())}`
    }
  }
}
</script>

<style scoped>
.visit-detail { padding-bottom:30px; }
.visit-summary { display:flex; justify-content:space-between; align-items:center; gap:15px; padding:19px 21px; background:linear-gradient(120deg,#edf8fd,#fff); border:1px solid #d9e8ef; border-radius:12px; }
.visit-summary small,.visit-summary strong,.visit-summary div>span { display:block; }
.visit-summary small { color:#738492; }
.visit-summary strong { margin:5px 0; font-size:21px; color:#243d51; overflow-wrap:anywhere; }
.visit-summary div>span { font-size:12px; color:#788997; }
.visit-facts { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:10px; margin:0; }
.visit-fact { min-width:0; padding:14px 16px; background:#fff; border:1px solid #dfe8ed; border-radius:9px; box-shadow:0 4px 15px rgba(28,69,91,.04); }
.visit-fact dt { margin-bottom:6px; color:#718391; font-size:12px; font-weight:600; }
.visit-fact dd { min-width:0; margin:0; color:#344754; line-height:1.6; overflow-wrap:anywhere; }
.visit-text-card { margin-bottom:10px; padding:16px 18px; background:#fff; border:1px solid #dfe8ed; border-radius:10px; box-shadow:0 4px 15px rgba(28,69,91,.04); }
.visit-text-card.summary-card { background:#f9fcff; border-color:#d5e7f1; }
.visit-text-card h4 { margin:0 0 9px; color:#607482; font-size:13px; }
.detail-text { color:#344754; line-height:1.8; white-space:pre-wrap; overflow-wrap:anywhere; }
.compliance-summary { margin-top:10px; }
.section-explain { margin:0 0 14px; color:#6d7f8b; font-size:12px; line-height:1.7; }
.round-card { margin:0 0 15px; padding:14px; border:1px solid #e2eaf0; border-radius:10px; background:#fff; }
.round-title { margin-bottom:12px; font-weight:700; color:#304758; }
.round-title small { margin-left:8px; font-weight:400; color:#8997a1; }
@media (max-width:900px) { .visit-facts { grid-template-columns:1fr; } }
</style>
